import type { ShoppingBagItem } from '@ori-events/basket-updated';
import { DiscountTagFilled, Products } from '@ori-ui/icons';
import { Link, Typography, styled } from '@ori-ui/mui';
import { Placement } from '@ori/analytics-data';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import { useCallback, useContext } from 'react';
import { FormattedMessage } from 'react-intl';

import { QuantityPicker } from './QuantityPicker';
import { Remove } from './Remove';
import { Shade } from './Shade';
import { miniBagContext } from '~/contexts';
import { translationKeys } from '~/models';
import type { MiniBagState, HandleQuantityChangeOptions } from '~/models';
import { removeBasketItem, showErrorNotification, updateBasketItem } from '~/utils';

const Root = styled('li')(({ theme }) => ({
  padding: theme.spacing(3, 0),
  position: 'relative',
  boxSizing: 'border-box',
  margin: theme.spacing(0, 3),
  '& + li': {
    borderTop: `2px solid ${theme.palette.grey[100]}`,
  },
}));

const StyledMiniBagWrapper = styled('div')({
  display: 'flex',
  color: '999',
  textDecoration: 'none',
});

const ImgLink = styled(Link)(({ theme }) => ({
  width: 80,
  height: 100,
  padding: theme.spacing(3, 0),
  marginRight: theme.spacing(2),
  background: theme.palette.grey[100],
  borderRadius: 5,
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  justifyContent: 'center',
}));

const Image = styled('img')({
  maxWidth: '100%',
  maxHeight: '100%',
});

const MissingImgIcon = styled(Products)(({ theme }) => ({
  position: 'absolute',
  width: '50%',
  height: 'auto',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  color: theme.palette.grey[200],
}));

const StyledItemDescription = styled('div')({
  display: 'flex',
  flexGrow: 1,
  flexDirection: 'column',
  textDecoration: 'none',
  maxWidth: 'calc(100% - 90px)',
});

const StyledItemInfoLink = styled(Link)(({ theme }) => ({
  display: 'flex',
  textDecoration: 'none',
  maxHeight: 40,
  overflow: 'hidden',
}));

const StyledName = styled(Typography)({
  maxWidth: 'calc(100% - 38px)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
});

const Discount = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  flexShrink: 0,
  width: 25,
  height: 25,
  marginLeft: 'auto',
  border: '0',
  color: theme.palette.offer.contrastText,
  borderRadius: '50%',
  backgroundColor: theme.palette.offer.main,
}));

const DiscountIcon = styled(DiscountTagFilled)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(15),
}));

const StyledItemDetails = styled('div')({
  display: 'flex',
  marginTop: 'auto',
  textDecoration: 'none',
});

const StyledQuantityBlock = styled(Typography)({
  marginRight: 'auto',
  alignSelf: 'flex-end',
});

const StyledPriceBlock = styled('div')({
  marginLeft: 'auto',
  marginTop: 'auto',
});

const StyledPrice = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
}));

const StyledPriceOld = styled(Typography)(({ theme }) => ({
  textAlign: 'right',
  textDecoration: 'line-through',
  fontSize: theme.typography.pxToRem(12),
  color: theme.palette.text.disabled,
}));

export const Item: FC<ShoppingBagItem> = ({
  itemCode,
  itemDescription,
  itemImageSrc,
  itemDetailPath,
  itemType,
  requestedQuantity,
  totalOriginalPrice,
  totalPrice,
  canBeDeleted,
  lineNumber,
  canBeModified,
  canBeReduced,
  maxQuantity,
  colorHexCode,
  colorImageSrc,
  itemColor,
}) => {
  const { getTestId } = useTestId();
  const { setMiniBagState } = useContext(miniBagContext);
  const setState = useCallback(
    (nextState: Partial<MiniBagState>) =>
      setMiniBagState((previousState) => ({
        ...previousState,
        ...nextState,
      })),
    [setMiniBagState],
  );
  const removeItemHandler = useCallback(async () => {
    try {
      setState({
        isLoading: true,
      });
      await removeBasketItem({
        itemCode,
        lineNumber,
        placement: Placement.Minibag,
        previousQuantity: requestedQuantity,
      });
    } catch (error: unknown) {
      // eslint-disable-next-line no-console -- want the info in console
      console.error(error);
      setState({
        isLoading: false,
      });
    }
  }, [setState, itemCode, lineNumber, requestedQuantity]);

  const handleQuantityChange = useCallback(
    async ({
      itemCode: code,
      lineNumber: line,
      quantity: currentQuantity,
      previousQuantity,
    }: HandleQuantityChangeOptions) => {
      if (currentQuantity === 0) {
        removeItemHandler();
      } else {
        try {
          await updateBasketItem({
            itemCode: code,
            lineNumber: line,
            placement: Placement.Minibag,
            previousQuantity,
            quantity: currentQuantity,
          });
        } catch (error) {
          showErrorNotification('genericError');
        }
      }
    },
    [removeItemHandler],
  );

  const partialId = `${lineNumber}-${itemCode}-${itemType}`;

  return (
    <Root
      id={`item-${partialId}-${totalPrice}`}
      data-testid={`${getTestId('item')}-${partialId}`}
    >
      {canBeDeleted ? (
        <Remove
          data-testid="remove"
          onClick={removeItemHandler}
        />
      ) : null}
      <StyledMiniBagWrapper>
        <ImgLink href={itemDetailPath}>
          {itemImageSrc ? (
            <Image
              src={itemImageSrc}
              alt="product"
            />
          ) : (
            <MissingImgIcon />
          )}
        </ImgLink>
        <StyledItemDescription>
          <StyledItemInfoLink href={itemDetailPath}>
            <StyledName variant="body1">{itemDescription}</StyledName>
            {totalOriginalPrice ? (
              <Discount>
                <DiscountIcon />
              </Discount>
            ) : null}
          </StyledItemInfoLink>
          {(colorHexCode || colorImageSrc) && itemColor ? (
            <Shade
              data-testid="shade"
              colorHexCode={colorHexCode}
              colorImageSrc={colorImageSrc}
              colorName={itemColor}
            />
          ) : null}
          <StyledItemDetails>
            {!canBeReduced && !canBeModified ? (
              <StyledQuantityBlock variant="body1">
                <FormattedMessage id={translationKeys.qty} />:{requestedQuantity}
              </StyledQuantityBlock>
            ) : (
              <QuantityPicker
                data-testid="quantity-picker"
                canBeDeleted={canBeDeleted}
                canBeModified={canBeModified}
                canBeReduced={canBeReduced}
                initialQuantity={requestedQuantity}
                itemCode={itemCode}
                lineNumber={lineNumber}
                maxQuantity={maxQuantity}
                onQuantityChange={handleQuantityChange}
              />
            )}
            <StyledPriceBlock>
              {totalOriginalPrice ? (
                <StyledPriceOld variant="body1">{totalOriginalPrice}</StyledPriceOld>
              ) : null}
              <StyledPrice variant="body2">{totalPrice}</StyledPrice>
            </StyledPriceBlock>
          </StyledItemDetails>
        </StyledItemDescription>
      </StyledMiniBagWrapper>
    </Root>
  );
};
